import { Body1, Button, Caption1, makeStyles, tokens } from '@fluentui/react-components';
import { ArrowUploadRegular, DeleteRegular, DocumentRegular } from '@fluentui/react-icons';

import { TAttachment } from '../../models';

const useStyles = makeStyles({
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  item: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM,
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalM}`,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  meta: {
    color: tokens.colorNeutralForeground3,
  },
});

type TProps = {
  attachments: TAttachment[];
  onAdd: () => void;
  onRemove: (id: string) => void;
  disabled?: boolean;
};

// A mock upload list. No real files are stored; "Add attachment" appends a
// placeholder entry so the interaction is visible in the prototype.
export const MockUploadList = ({ attachments, onAdd, onRemove, disabled }: TProps) => {
  const classes = useStyles();
  return (
    <div className="flex flex-col gap-3">
      {attachments.length > 0 ? (
        <div className={classes.list}>
          {attachments.map((attachment) => (
            <div key={attachment.id} className={classes.item}>
              <div className="flex items-center gap-2 min-w-0">
                <DocumentRegular />
                <div className="flex flex-col min-w-0">
                  <Body1 className="truncate">{attachment.name}</Body1>
                  <Caption1 className={classes.meta}>
                    {attachment.sizeLabel} · {attachment.uploadedBy}
                  </Caption1>
                </div>
              </div>
              {!disabled ? (
                <Button
                  appearance="subtle"
                  icon={<DeleteRegular />}
                  aria-label={`Remove ${attachment.name}`}
                  onClick={() => onRemove(attachment.id)}
                />
              ) : null}
            </div>
          ))}
        </div>
      ) : (
        <Caption1 className={classes.meta}>No attachments added.</Caption1>
      )}
      {!disabled ? (
        <Button appearance="secondary" icon={<ArrowUploadRegular />} onClick={onAdd} className="w-fit">
          Add attachment
        </Button>
      ) : null}
    </div>
  );
};
